/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Compound card component with 4 variants and structural sub-components
 *   PROPS:   variant (default|compact|promo|interactive)
 *   STATE:   none
 *   DEPENDS: M-CN (cn utility)
 *   EMITS:   native div events via spread props
 *   ACCESSIBILITY: semantic HTML structure via sub-components
 *   LINKS:   M-CRD
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-12] Added GRACE MODULE_CONTRACT and semantic blocks — no behavior change
 */

import { forwardRef } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/cn"

// --- VariantDefinitions ---
const cardVariants = cva(
  "border border-hairline-dark bg-surface-card-dark transition-colors duration-150",
  {
    variants: {
      variant: {
        default: "rounded-xl p-6",
        compact: "rounded-lg p-4",
        promo: "rounded-xl p-8",
        interactive: "rounded-xl p-6 hover:bg-surface-elevated-dark",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

// --- TypeDefinitions ---
interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

// --- CardRoot ---
const Card = forwardRef<HTMLDivElement, CardProps>(({ className, variant, ...props }, ref) => {
  return <div ref={ref} className={cn(cardVariants({ variant, className }))} {...props} />
})
Card.displayName = "Card"

// --- CardHeader ---
const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn("mb-4", className)} {...props} />
  },
)
CardHeader.displayName = "CardHeader"

// --- CardTitle ---
const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => {
    return (
      <h3 ref={ref} className={cn("text-lg font-semibold text-on-dark", className)} {...props} />
    )
  },
)
CardTitle.displayName = "CardTitle"

// --- CardDescription ---
const CardDescription = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => {
    return <p ref={ref} className={cn("mt-1 text-sm text-muted", className)} {...props} />
  },
)
CardDescription.displayName = "CardDescription"

// --- CardContent ---
const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn("", className)} {...props} />
  },
)
CardContent.displayName = "CardContent"

export { Card, CardHeader, CardTitle, CardDescription, CardContent, cardVariants }
export type { CardProps }
